/**
 * Task #1307 — Knowledge Base capabilities (capabilities-driven UI).
 *
 * Tipo condiviso FE/BE: il backend espone `GET /api/knowledge-base/_capabilities/:agentId`
 * e ritorna questo payload, che è la SINGOLA fonte di verità per:
 *  - `accept` HTML del file input (`acceptedExtensions`)
 *  - banner pipeline attiva (`pipelineLabel` + tooltip)
 *  - sezione "Tipologie supportate" (`supportedKinds`)
 *  - badge Firecrawl (`firecrawlAvailable`)
 *  - warning incoerenza chat model ↔ multimodal docs (`warnings` + `multimodalDocsCount`)
 *
 * Nessun client deve inferire formati/pipeline da `tenant_settings` direttamente.
 */
import { z } from 'zod';

import type { EmbedderProvider } from '../embedder-capabilities';

export type OcrProvider = 'openai' | 'claude' | 'google' | 'none';

/**
 * Etichetta pipeline ingestion attiva per il tenant+agent corrente.
 * - `text_only`            → embedder=openai, ingest testo classico (no OCR/multimodal)
 * - `text_plus_ocr`        → embedder=openai, OCR provider configurato per PDF scansionati
 * - `gemini_multimodal_native`        → embedder=gemini, file image/audio/video letti nativi
 * - `gemini_multimodal_pdf_rasterize` → embedder=gemini + image_chunks_enabled, PDF rasterizzati pagina-per-pagina
 */
export type PipelineLabel =
  | 'text_only'
  | 'text_plus_ocr'
  | 'gemini_multimodal_native'
  | 'gemini_multimodal_pdf_rasterize';

export interface CapabilityFlags {
  audio_supported: boolean;
  video_supported: boolean;
  image_standalone_supported: boolean;
  pdf_rasterize_supported: boolean;
  firecrawl_available: boolean;
}

export type CapabilityWarningCode =
  | 'OCR_GEMINI_INCOHERENT'
  | 'EMBEDDER_NOT_CONFIGURED'
  | 'CHAT_MODEL_NOT_GEMINI_WITH_MULTIMODAL_DOCS'
  | 'FIRECRAWL_NOT_CONFIGURED';

export interface CapabilityWarning {
  code: CapabilityWarningCode;
  severity: 'info' | 'warning' | 'error';
  message: string;
}

export interface SupportedKindGroup {
  label: string;
  extensions: ReadonlyArray<string>;
}

export interface KnowledgeBaseCapabilities {
  agentId: string;
  resolvedAgentId: string;
  embedder: {
    provider: EmbedderProvider;
    model: string;
    configured: boolean;
  };
  ocr: {
    provider: OcrProvider;
  };
  chatModel: {
    model: string | null;
    isGemini: boolean;
  };
  pipelineLabel: PipelineLabel;
  /** Frase corta human-readable mostrata nel tooltip del banner. */
  pipelineTooltip: string;
  /** MIME types accettati dall'upload (whitelist effettiva). */
  acceptedMimes: ReadonlyArray<string>;
  /** Estensioni accettate (con il `.`) — pronte per `<input accept="...">`. */
  acceptedExtensions: ReadonlyArray<string>;
  /** Raggruppamento per UI ("Documenti", "Immagini", "Audio", "Video"). */
  supportedKinds: ReadonlyArray<SupportedKindGroup>;
  capabilityFlags: CapabilityFlags;
  /** Warning attivi (incoerenze, configurazioni mancanti). */
  warnings: ReadonlyArray<CapabilityWarning>;
  /**
   * Numero di documenti già ingeriti che dipendono dalla pipeline multimodale Gemini
   * (PDF con image chunks oppure media standalone). Usato dalla UI per mostrare
   * il warning "cambiando chat model perdi N doc multimodali".
   */
  multimodalDocsCount: number;
  /**
   * Documenti contati in `multimodalDocsCount` (id + nome filename). Usato
   * dalla UI per mostrare nel dialog di conferma cambio chat model l'elenco
   * dei doc impattati e per lanciare il bulk re-ingest senza una seconda
   * round-trip (e per filtrare i bottoni "Re-ingest" per riga).
   */
  multimodalDocs: ReadonlyArray<{ id: string; name: string }>;
  /** Flag derivato per UX. */
  firecrawlAvailable: boolean;
  /** Timestamp ISO di calcolo (lato server) per debug. */
  computedAt: string;
}

export const KnowledgeBaseCapabilitiesSchema = z.object({
  agentId: z.string(),
  resolvedAgentId: z.string(),
  embedder: z.object({
    provider: z.enum(['openai', 'gemini']),
    model: z.string(),
    configured: z.boolean(),
  }),
  ocr: z.object({
    provider: z.enum(['openai', 'claude', 'google', 'none']),
  }),
  chatModel: z.object({
    model: z.string().nullable(),
    isGemini: z.boolean(),
  }),
  pipelineLabel: z.enum([
    'text_only',
    'text_plus_ocr',
    'gemini_multimodal_native',
    'gemini_multimodal_pdf_rasterize',
  ]),
  pipelineTooltip: z.string(),
  acceptedMimes: z.array(z.string()).readonly(),
  acceptedExtensions: z.array(z.string()).readonly(),
  supportedKinds: z
    .array(
      z.object({
        label: z.string(),
        extensions: z.array(z.string()).readonly(),
      })
    )
    .readonly(),
  capabilityFlags: z.object({
    audio_supported: z.boolean(),
    video_supported: z.boolean(),
    image_standalone_supported: z.boolean(),
    pdf_rasterize_supported: z.boolean(),
    firecrawl_available: z.boolean(),
  }),
  warnings: z
    .array(
      z.object({
        code: z.enum([
          'OCR_GEMINI_INCOHERENT',
          'EMBEDDER_NOT_CONFIGURED',
          'CHAT_MODEL_NOT_GEMINI_WITH_MULTIMODAL_DOCS',
          'FIRECRAWL_NOT_CONFIGURED',
        ]),
        severity: z.enum(['info', 'warning', 'error']),
        message: z.string(),
      })
    )
    .readonly(),
  multimodalDocsCount: z.number().int().nonnegative(),
  multimodalDocs: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .readonly(),
  firecrawlAvailable: z.boolean(),
  computedAt: z.string(),
});

export interface KnowledgeBaseCapabilitiesErrorResponse {
  success: false;
  error: string;
  code: 'CAPABILITIES_AGENT_NOT_FOUND' | 'CAPABILITIES_TENANT_NOT_CONFIGURED';
}

export interface KnowledgeBaseCapabilitiesSuccessResponse {
  success: true;
  data: KnowledgeBaseCapabilities;
}

export type KnowledgeBaseCapabilitiesResponse =
  | KnowledgeBaseCapabilitiesSuccessResponse
  | KnowledgeBaseCapabilitiesErrorResponse;
